import * as React from 'react';
import clsx from 'clsx';
import { CypressI } from 'interfaces/cypress';

export type TypographyType =
	| 'custom-p'
	| 'custom-h1'
	| 'custom-h2'
	| 'custom-h3'
	| 'custom-h4'

	// Figma styles (Adapt to your own styles)
	| 'headline-1'
	| 'headline-2'
	| 'headline-3'
	| 'headline-4'
	| 'headline-5'
	| 'headline-42'
	| 'headline-48'
	| 'regular-9'
	| 'regular-10'
	| 'regular-12'
	| 'regular-13'
	| 'regular-14'
	| 'regular-16'
	| 'regular-18'
	| 'regular-20'
	| 'regular-24'
	| 'regular-28'
	| 'medium-12'
	| 'medium-14'
	| 'semibold-12'
	| 'semibold-14'
	| 'semibold-16'
	| 'semibold-18'
	| 'semibold-20'
	| 'semibold-24'
	| 'semibold-28'
	| 'semibold-30'
	| 'semibold-32'
	| 'semibold-36'
	| 'bold-12'
	| 'bold-16'
	| 'bold-18'
	| 'bold-20'
	| 'bold-22'
	| 'bold-24'
	| 'bold-28'
	| 'bold-30'
	| 'bold-36'
	| 'subtitle-1'
	| 'subtitle-2'
	| 'subtitle-3'
	| 'link-1'
	| 'link-2'
	| 'body-1'
	| 'body-2'
	| 'caption-1'
	| 'caption-2'
	| 'caption-3'
	| 'overline';

export interface TypographyProps {
	id?: string;
	// Custom tags
	type: TypographyType;

	text?: string;
	className?: string;
	children?: React.ReactNode;
	style?: React.CSSProperties;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onClick?: (data?: any) => void;
	html?: string;
}

export const Typography: React.FC<TypographyProps & CypressI> = ({
	id,
	dataCY,
	type,
	text = '',
	className = '',
	style,
	children,
	onClick,
	html,
}) => {
	//              //
	//              //
	// Custom Tags  //
	if (html) {
		return (
			<p
				id={id}
				style={{ display: 'block' }}
				className={className}
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	if (type === 'custom-p')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</p>
		);
	if (type === 'custom-h1')
		return (
			<h1
				id={id}
				data-cy={dataCY}
				className={clsx(className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h1>
		);
	if (type === 'custom-h2')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx('text-h2 font-bold', className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);
	if (type === 'custom-h3')
		return (
			<h3
				id={id}
				data-cy={dataCY}
				className={clsx(className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h3>
		);
	if (type === 'custom-h4')
		return (
			<h4
				id={id}
				data-cy={dataCY}
				className={clsx(className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h4>
		);

	/////////////////////////
	//                    //
	// Figma Text Styles  //
	if (type === 'headline-1')
		return (
			<h1
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-bold text-[40px] lg:text-5xl',
					'md:!text-head-1',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h1>
		);
	if (type === 'headline-2')
		return (
			<h3
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-bold',
					'text-3xl md:text-head-2',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h3>
		);
	if (type === 'headline-3')
		return (
			<h3
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-bold text-[32px]',
					'md:text-4xl md:leading-[39.6px]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h3>
		);

	if (type === 'headline-4')
		return (
			<h4
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-bold text-3xl',
					'md:[2.25rem] md:leading-[2.475rem]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h4>
		);

	if (type === 'headline-5')
		return (
			<h5
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-bold text-lg',
					'xl:text-[21px] xl:leading-[26px]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h5>
		);

	if (type === 'headline-42')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-[#0D0616] font-bold text-[42px]', className)}
			>
				{children || text}
			</p>
		);

	if (type === 'headline-48')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-[#0D0616] font-bold text-3xl lg:text-[48px]',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-9')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-normal text-xs xl:text-[9px]',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-10')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-normal text-[10px]', className)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-12')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-normal text-xs', className)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-13')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-normal text-xs xl:text-[13px] leading-[11px]',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-14')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-normal text-sm xl:text-sm', className)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-16')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-normal text-base', className)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-18')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-normal text-base lg:text-lg',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-20')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-normal text-base md:text-xl',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-24')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-normal text-base xl:text-2xl',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'regular-28')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-normal text-lg xl:text-[28px]',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'medium-12')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-medium text-sm xl:text-xs ',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'medium-14')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-medium text-sm xl:text-sm leading-[19px]',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-12')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-semibold text-xs', className)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-14')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-semibold text-sm leading-[16.8px]',
					className
				)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-16')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'font-semibold text-black text-sm lg:text-base',
					className
				)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-18')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('font-semibold text-sm lg:text-lg', className)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-20')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('font-semibold text-lg lg:text-xl', className)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-24')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-semibold text-2xl', className)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-28')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-semibold text-2xl xl:text-[28px]',
					className
				)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-30')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-semibold text-xl lg:text-[30px]',
					className
				)}
				style={style}
			>
				{children || text}
			</p>
		);

	if (type === 'semibold-32')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-semibold text-[32px]', className)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'semibold-36')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black font-semibold text-[36px]', className)}
				style={style}
			>
				{children || text}
			</p>
		);
	if (type === 'bold-12')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx('text-black !font-bold text-xs xl:text-xs', className)}
			>
				{children || text}
			</span>
		);
	if (type === 'bold-16')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black !font-bold text-base xl:text-base',
					className
				)}
			>
				{children || text}
			</span>
		);
	if (type === 'bold-18')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx('text-black !font-bold text-lg xl:text-lg', className)}
			>
				{children || text}
			</span>
		);

	if (type === 'bold-20')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-bold text-xl xl:text-[20px]',
					className
				)}
			>
				{children || text}
			</span>
		);
	if (type === 'bold-22')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black font-semibold lg:!font-bold text-base lg:text-[22px]',
					className
				)}
			>
				{children || text}
			</span>
		);
	if (type === 'bold-24')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx('text-black !font-bold text-xl xl:text-2xl', className)}
			>
				{children || text}
			</span>
		);
	if (type === 'bold-28')
		return (
			<span
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black !font-bold text-2xl xl:text-[28px]',
					className
				)}
			>
				{children || text}
			</span>
		);
	if (type === 'bold-30')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx('text-black !font-bold text-xl xl:text-3xl', className)}
			>
				{children || text}
			</p>
		);
	if (type === 'bold-36')
		return (
			<p
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-black !font-bold text-3xl lg:text-4xl',
					className
				)}
			>
				{children || text}
			</p>
		);
	if (type === 'subtitle-1')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-medium text-base xl:text-lg',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'subtitle-2')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-medium text-sm xl:text-base',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'subtitle-3')
		return (
			<h3
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-bold text-sm xl:text-base leading-5',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h3>
		);

	if (type === 'link-1')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx('text-gray-800 font-bold text-sm', className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'link-2')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx('text-gray-800 font-bold text-xs', className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'body-1')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx('text-gray-800 font-normal text-base', className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'body-2')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx('text-gray-800 font-normal text-xs', className)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'caption-1')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-light text-[10px] leading-[14px]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'caption-2')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-medium text-[10px] leading-[14px]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'caption-3')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-extrabold text-[10px] leading-[14px]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	if (type === 'overline')
		return (
			<h2
				id={id}
				data-cy={dataCY}
				className={clsx(
					'text-gray-800 font-medium text-[8px] leading-[10px]',
					className
				)}
				onClick={() => onClick && onClick()}
			>
				{children || text}
			</h2>
		);

	return null;
};
